<script lang="ts">
	import { page } from '$app/stores';
	import { cn } from '$lib/utils';
	import {
		Calendar as CalendarIcon,
		Radio as RadioIcon,
		BookOpen,
		ChevronDown,
		Home,
		Info,
		Users,
		MessageCircle,
		Mail,
		Rss
	} from 'lucide-svelte';
	import clickOutside from '$lib/actions/clickOutside';

	export let membershipOpen = false;
	export let resourcesOpen = false;
	export let netsOpen = false;
	export let calendarOpen = false;
</script>

<div class="hidden md:ml-6 md:flex md:space-x-8">
	<a
		href="/"
		class={cn(
			'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium',
			$page.url.pathname === '/'
				? 'bg-blue-900 dark:bg-blue-800'
				: 'hover:bg-blue-700 dark:hover:bg-blue-800'
		)}
	>
		<Home class="mr-2 h-4 w-4" />
		Home
	</a>
	<a
		href="/about"
		class={cn(
			'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium',
			$page.url.pathname === '/about'
				? 'bg-blue-900 dark:bg-blue-800'
				: 'hover:bg-blue-700 dark:hover:bg-blue-800'
		)}
	>
		<Info class="mr-2 h-4 w-4" />
		About
	</a>

	<!-- Membership dropdown -->
	<div class="relative" use:clickOutside on:outclick={() => (membershipOpen = false)}>
		<button
			id="membership-button"
			type="button"
			class={cn(
				'group inline-flex items-center rounded-md px-3 py-2 text-sm font-medium',
				$page.url.pathname.startsWith('/membership') || $page.url.pathname.startsWith('/meetings')
					? 'bg-blue-900 dark:bg-blue-800'
					: 'hover:bg-blue-700 dark:hover:bg-blue-800'
			)}
			on:click={() => (membershipOpen = !membershipOpen)}
			aria-expanded={membershipOpen}
		>
			<Users class="mr-2 h-4 w-4" />
			<span>Membership</span>
			<ChevronDown
				class={cn('ml-2 h-5 w-5 transition-transform', membershipOpen && 'rotate-180')}
				aria-hidden="true"
			/>
		</button>

		{#if membershipOpen}
			<div
				id="membership-dropdown"
				class="ring-opacity-5 absolute -left-2 z-10 mt-2 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-none dark:bg-gray-800"
				role="menu"
				aria-orientation="vertical"
				tabindex="-1"
			>
				<div class="py-1" role="none">
					<a
						href="/membership"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname === '/membership'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (membershipOpen = false)}
					>
						Info/Join/Renew
					</a>
					<a
						href="/meetings"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname.startsWith('/meetings')
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (membershipOpen = false)}
					>
						Meeting Minutes
					</a>
				</div>

				<div class="py-1" role="none">
					<p class="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
						Club Documents
					</p>
					<a
						href="/membership/constitution"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname === '/membership/constitution'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (membershipOpen = false)}
					>
						Constitution
					</a>
					<a
						href="/membership/bylaws"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname === '/membership/bylaws'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (membershipOpen = false)}
					>
						Club Bylaws
					</a>
				</div>

				<div class="py-1" role="none">
					<p class="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
						Corporate Documents
					</p>
					<a
						href="/membership/dsarc-inc/bylaws"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname === '/membership/dsarc-inc/bylaws'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (membershipOpen = false)}
					>
						Corporate Bylaws
					</a>
				</div>

				<div class="py-1" role="none">
					<p class="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">AUXCOMM</p>
					<a
						href="/membership/auxcomm/bylaws"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname === '/membership/auxcomm/bylaws'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (membershipOpen = false)}
					>
						AUXCOMM Bylaws
					</a>
				</div>
			</div>
		{/if}
	</div>

	<!-- Nets dropdown -->
	<div class="relative" use:clickOutside on:outclick={() => (netsOpen = false)}>
		<button
			id="nets-button"
			type="button"
			class={cn(
				'group inline-flex items-center rounded-md px-3 py-2 text-sm font-medium',
				$page.url.pathname.startsWith('/nets')
					? 'bg-blue-900 dark:bg-blue-800'
					: 'hover:bg-blue-700 dark:hover:bg-blue-800'
			)}
			on:click={() => (netsOpen = !netsOpen)}
			aria-expanded={netsOpen}
		>
			<RadioIcon class="mr-2 h-4 w-4" />
			<span>Nets</span>
			<ChevronDown
				class={cn('ml-2 h-5 w-5 transition-transform', netsOpen && 'rotate-180')}
				aria-hidden="true"
			/>
		</button>

		{#if netsOpen}
			<div
				id="nets-dropdown"
				class="ring-opacity-5 absolute -left-2 z-10 mt-2 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-none dark:bg-gray-800"
				role="menu"
				aria-orientation="vertical"
				tabindex="-1"
			>
				<a
					href="/nets/packet"
					class={cn(
						'block px-4 py-2 text-sm',
						$page.url.pathname === '/nets/packet'
							? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
							: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
					)}
					role="menuitem"
					tabindex="-1"
					on:click={() => (netsOpen = false)}
				>
					Packet Radio
				</a>
				<a
					href="/nets/auxcomm_ares"
					class={cn(
						'block px-4 py-2 text-sm',
						$page.url.pathname === '/nets/auxcomm_ares'
							? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
							: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
					)}
					role="menuitem"
					tabindex="-1"
					on:click={() => (netsOpen = false)}
				>
					AUXCOMM/ARES
				</a>
			</div>
		{/if}
	</div>

	<!-- Calendar dropdown -->
	<div class="relative" use:clickOutside on:outclick={() => (calendarOpen = false)}>
		<button
			id="calendar-button"
			type="button"
			class={cn(
				'group inline-flex items-center rounded-md px-3 py-2 text-sm font-medium',
				$page.url.pathname.startsWith('/calendar') ||
					$page.url.pathname.startsWith('/events') ||
					$page.url.pathname === '/newsletter'
					? 'bg-blue-900 dark:bg-blue-800'
					: 'hover:bg-blue-700 dark:hover:bg-blue-800'
			)}
			on:click={() => (calendarOpen = !calendarOpen)}
			aria-expanded={calendarOpen}
		>
			<CalendarIcon class="mr-2 h-4 w-4" />
			<span>Events</span>
			<ChevronDown
				class={cn('ml-2 h-5 w-5 transition-transform', calendarOpen && 'rotate-180')}
				aria-hidden="true"
			/>
		</button>

		{#if calendarOpen}
			<div
				id="calendar-dropdown"
				class="ring-opacity-5 absolute left-0 z-10 mt-2 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-none dark:bg-gray-800"
				role="menu"
				aria-orientation="vertical"
				tabindex="-1"
			>
				<div class="py-1" role="none">
					<a
						href="/calendar"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname === '/calendar'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (calendarOpen = false)}
					>
						Calendar
					</a>
					<a
						href="/events"
						class={cn(
							'block px-4 py-2 text-sm',
							$page.url.pathname === '/events'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (calendarOpen = false)}
					>
						Events
					</a>
				</div>

				<div class="py-1" role="none">
					<p class="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
						Stay Connected
					</p>
					<a
						href="/newsletter"
						class={cn(
							'flex items-center px-4 py-2 text-sm',
							$page.url.pathname === '/newsletter'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (calendarOpen = false)}
					>
						<Mail class="mr-2 h-4 w-4" />
						Newsletter
					</a>
					<a
						href="/feeds"
						class={cn(
							'flex items-center px-4 py-2 text-sm',
							$page.url.pathname === '/feeds'
								? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
								: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
						)}
						role="menuitem"
						tabindex="-1"
						on:click={() => (calendarOpen = false)}
					>
						<Rss class="mr-2 h-4 w-4" />
						Subscribe
					</a>
				</div>
			</div>
		{/if}
	</div>

	<!-- Resources dropdown -->
	<div class="relative" use:clickOutside on:outclick={() => (resourcesOpen = false)}>
		<button
			id="resources-button"
			type="button"
			class={cn(
				'group inline-flex items-center rounded-md px-3 py-2 text-sm font-medium',
				$page.url.pathname.startsWith('/resources')
					? 'bg-blue-900 dark:bg-blue-800'
					: 'hover:bg-blue-700 dark:hover:bg-blue-800'
			)}
			on:click={() => (resourcesOpen = !resourcesOpen)}
			aria-expanded={resourcesOpen}
		>
			<BookOpen class="mr-2 h-4 w-4" />
			<span>Resources</span>
			<ChevronDown
				class={cn('ml-2 h-5 w-5 transition-transform', resourcesOpen && 'rotate-180')}
				aria-hidden="true"
			/>
		</button>

		{#if resourcesOpen}
			<div
				id="resources-dropdown"
				class="ring-opacity-5 absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-none dark:bg-gray-800"
				role="menu"
				aria-orientation="vertical"
				tabindex="-1"
			>
				<a
					href="/resources/repeaters"
					class={cn(
						'flex items-center px-4 py-2 text-sm',
						$page.url.pathname === '/resources/repeaters'
							? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
							: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
					)}
					role="menuitem"
					tabindex="-1"
					on:click={() => (resourcesOpen = false)}
				>
					<RadioIcon class="mr-2 h-4 w-4" />
					Repeater List
				</a>
				<a
					href="/resources/testing"
					class={cn(
						'flex items-center px-4 py-2 text-sm',
						$page.url.pathname === '/resources/testing'
							? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
							: 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
					)}
					role="menuitem"
					tabindex="-1"
					on:click={() => (resourcesOpen = false)}
				>
					<RadioIcon class="mr-2 h-4 w-4" />
					License Testing
				</a>
			</div>
		{/if}
	</div>

	<a
		href="/contact"
		class={cn(
			'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium',
			$page.url.pathname === '/contact'
				? 'bg-blue-900 dark:bg-blue-800'
				: 'hover:bg-blue-700 dark:hover:bg-blue-800'
		)}
	>
		<MessageCircle class="mr-2 h-4 w-4" />
		Contact
	</a>
</div>
